import { Autocomplete, TextField } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import React, { useEffect } from 'react';
import {
	Controller,
	FieldValues,
	Path,
	UseControllerProps,
	useFormContext,
	useWatch,
} from 'react-hook-form';
import { LookupFieldConfiguration } from '../../../api/ConfigApi';
import TabularDatasourcesApi from '../../../api/TabularDatasourcesApi';
import { AuthData } from '../../../context/auth';
import FieldLabel from '../../fields/FieldLabel';

type FieldProps = Omit<LookupFieldConfiguration, 'name'>;
interface Props<T> extends FieldProps, UseControllerProps<T> {
	form: string;
	configVersion: string;
	productName: string;
	authData: AuthData;
	save?: (currValue: string, fieldName: string) => void;
	fieldHasExternalDataCalls?: boolean;
	groupFieldName?: string;
	effectiveDate?: string;
}

const LookupField = <T extends FieldValues>(props: Props<T>) => {
	const {
		form,
		control,
		name,
		title,
		optional,
		table,
		match,
		authData: {
			auth: { authorizationToken, configProperties },
		},
		configVersion,
		disabled,
		save,
		fieldHasExternalDataCalls,
		groupFieldName,
		productName,
		effectiveDate,
	} = props;

	const conditionalFieldDependencies = match.map(
		(path) => `${form}.${groupFieldName ? groupFieldName + '.' : ''}${path}`,
	) as Path<T>[];

	const watchFields = useWatch({
		control,
		name: conditionalFieldDependencies,
	});

	const { setValue, getValues } = useFormContext();

	const [loading, setLoading] = React.useState(true);
	const [options, setOptions] = React.useState<string[]>([]);

	useEffect(() => {
		const fetchOptions = async () => {
			setLoading(true);
			const pvEnabled =
				configProperties['property.product.versioning.m2.enabled'] ||
				configProperties['property.product.versioning.with.repair.enabled'];
			// If PV is enabled, we need to pass the configVersion to the API
			// Do not pass config version if PV is OFF
			const passedConfigVersion = pvEnabled ? configVersion : null;
			try {
				const columns: string[] =
					watchFields && watchFields.length ? watchFields : [];
				const freshOptions =
					(await TabularDatasourcesApi.getAllowedOptionsForTable(
						authorizationToken,
						table,
						columns,
						passedConfigVersion,
						productName,
						effectiveDate,
					)) as any;
				freshOptions.data
					? setOptions(freshOptions.data.values.sort())
					: setOptions([]);
			} catch (e) {
				setOptions([]);
			}
		};
		fetchOptions();
	}, [authorizationToken, configProperties, configVersion, table, watchFields]);

	useEffect(() => {
		if (Array.isArray(options)) {
			setLoading(false);

			if (!optional && options.length === 1 && getValues(name) === '') {
				setValue(name, options[0] as any, { shouldDirty: true });
				// trigger a save to fetch data from external data call, if applicable
				if (fieldHasExternalDataCalls) {
					save && save(options[0], name);
				}
			}
		}
		// if only one option is available, set it as the value (should only occur once)
	}, [
		options,
		save,
		setValue,
		getValues,
		fieldHasExternalDataCalls,
		name,
		optional,
	]);

	return (
		<Controller
			control={control}
			name={name}
			render={({ field, fieldState: { error } }) => (
				<FormControl error={!!error} sx={{ width: '100%' }}>
					<FieldLabel title={title} required={!optional} htmlFor={name} />
					<Autocomplete
						{...field}
						onChange={(_event, newValue) => {
							field.onChange(newValue);
						}}
						id={name}
						value={field.value || null}
						options={optional ? ['', ...options] : options}
						getOptionLabel={(option) => option ?? ''}
						disabled={loading || disabled || !options || !options.length}
						disableClearable={true}
						openOnFocus={true}
						autoHighlight={true}
						isOptionEqualToValue={(option, value) => {
							return option === '' ? false : option === value;
						}}
						renderOption={(props, option) => {
							// for optional fields, render a blank option
							return (
								<MenuItem {...props} value={option}>
									{option === '' ? <>&nbsp;</> : option}
								</MenuItem>
							);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								fullWidth
								required={!optional}
								error={!!error}
								helperText={error?.message}
								disabled={disabled}
								onBlur={(e) => {
									field.onBlur();
									save && save(e.target.value, name);
								}}
								sx={{
									'& .MuiInput-input:focus': {
										backgroundColor: 'transparent',
									},
								}}
							/>
						)}
					/>
				</FormControl>
			)}
		/>
	);
};

export default LookupField;
